import React, { useContext, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import AuthContext from "../../../context/AuthContext";
import { useConfirmModal } from "../../confirmModal/useConfirmModal";
import { useEffectOnce } from "../../../utils/effects";
import DataTable from "../../dataTable/DataTable";
import ListSearchField from "../../listSearchField/ListSearchField";
import {
  getEnrollmentLoadList,
  deleteEnrollmentLoad,
} from "./LoadEnrollment.thunk";
import { SetEnrollmentSelected } from "./LoadEnrollment.slice";
import {
  LoadEnrollmentRootArea,
  GridContainer,
  GridItem,
} from "./LoadEnrollment.styles";

/**
 * Shared In Progress enrollment table with search (Acquirer / ISO / Merchant drafts).
 */
const LoadEnrollmentInProgressTable = ({
  enrollmentType,
  columns,
  onContinue,
  deleteLabelField,
  searchPlaceholder = "Search drafts",
  title,
}) => {
  const dispatch = useDispatch();
  const { enrollmentData, page, pageSize, totalRecords, status } = useSelector(
    (state) => state.loadEnrollment,
  );
  const { userInfo, token } = useContext(AuthContext);
  const { email } = userInfo;
  const { showConfirmModal } = useConfirmModal();
  const [searchValue, setSearchValue] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const buildListPayload = (overrides = {}) => {
    const search = "search" in overrides ? overrides.search : appliedSearch;
    return {
      token,
      email,
      page: overrides.page ?? page,
      page_size: overrides.page_size ?? pageSize,
      enrollment_type: enrollmentType,
      ...(search && { search }),
    };
  };

  const loadList = (overrides = {}) => {
    dispatch(getEnrollmentLoadList(buildListPayload(overrides)));
  };

  useEffectOnce(() => {
    loadList();
  });

  const handleSearch = () => {
    const nextSearch = searchValue.trim();
    setAppliedSearch(nextSearch);
    loadList({ page: 1, search: nextSearch });
  };

  const handleSetToComplete = (row) => {
    dispatch(SetEnrollmentSelected({ enrollmentSelected: row.id }));
    onContinue(row);
  };

  const deleteItem = ({ id }) => {
    dispatch(deleteEnrollmentLoad({ token, id }));
    loadList({ page: 1 });
  };

  const handleDelete = (row) => {
    const label = row[deleteLabelField] || row.id;
    showConfirmModal({
      message: `Are you sure you want to delete the item ${label}?`,
      actionId: `delete-enrollment-${enrollmentType}-${row.id}`,
      title: "Please confirm",
      onConfirm: () => deleteItem({ id: row.id }),
    });
  };

  const handlePageChange = (event, newPage) => {
    loadList({ page: newPage + 1 });
  };

  const handleRowsPerPageChange = (event) => {
    const newPageSize = +event.target.value;
    loadList({ page: 1, page_size: newPageSize });
  };

  const tableColumns = columns.map((column) => {
    if (column.id !== "action") return column;
    return {
      ...column,
      actions: column.actions.map((action) => {
        if (action.label === "Set to Complete") {
          return {
            ...action,
            onClick: (row) => handleSetToComplete(row),
          };
        }
        if (action.label === "Delete") {
          return {
            ...action,
            onClick: (row) => handleDelete(row),
          };
        }
        return action;
      }),
    };
  });

  return (
    <LoadEnrollmentRootArea>
      <GridContainer>
        {title ? (
          <GridItem xs={12}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {title}
            </Typography>
          </GridItem>
        ) : null}
        <GridItem xs={12}>
          <Box sx={{ mb: 2, maxWidth: { xs: "100%", sm: 360 } }}>
            <ListSearchField
              fullWidth
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onSearch={handleSearch}
              placeholder={searchPlaceholder}
            />
          </Box>
          <DataTable
            loading={status === "loading"}
            columns={tableColumns}
            rows={enrollmentData}
            page={page - 1}
            rowsPerPage={pageSize}
            count={totalRecords}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            noDataText="No in-progress enrollments found"
          />
        </GridItem>
      </GridContainer>
    </LoadEnrollmentRootArea>
  );
};

export default LoadEnrollmentInProgressTable;
